import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {from, Observable, throwError} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {LoaderService, ToastService} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {ApiService} from '@services/api.service';
import {TranslateService} from '@ngx-translate/core';
import {LoginService} from '@services/login.service';
import {Router} from '@angular/router';
import {toastErrorCodesHidden} from '@constants/toast.constants';

@Injectable({
  providedIn: 'root'
})
export class HttpAuthorizationInterceptor implements HttpInterceptor {

  constructor(
    private loaderService: LoaderService,
    private apiService: ApiService,
    private translate: TranslateService,
    private toastService: ToastService,
    private router: Router,
    private loginService: LoginService
  ) {
  }

  public intercept(request: HttpRequest<any>, handler: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.startsWith('/')) {
      return handler.handle(request);
    }

    this.activateLoader(request);
    request = this.setBackUrl(request);
    request = this.setLanguage(request);
    request = this.setSecurity(request);

    return handler.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          event = this.interceptResponse(request, event);
        }
        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        // Handle error if Blob
        if (error.error instanceof Blob) {
          return this.interceptBlobErrorResponse(request, error);
        }

        // Handle error normally
        const newError = this.interceptErrorResponse(request, error);
        return throwError(newError);
      })
    );
  }

  interceptResponse(request: HttpRequest<any>, eventResponse: HttpEvent<any>): HttpEvent<any> {
    this.activateLoader(request, false);
    return eventResponse;
  }

  interceptBlobErrorResponse(request: HttpRequest<any>, error: HttpErrorResponse): Observable<never> {
    return from(error.error.text()).pipe(
      switchMap((errorText: string) => {
        let parsedError;
        // Try to parse the text as JSON
        try {
          parsedError = JSON.parse(errorText);
        } catch (jsonParseError) {
          console.error('Failed to parse Blob to JSON:', jsonParseError);
          parsedError = {message: 'Server response error, failed to parse.'};
        }

        // Create a new error object with the parsed error data
        const newError = {
          ...error,
          error: parsedError
        };

        // Handle parsed error normally
        return throwError(this.interceptErrorResponse(request, newError));
      })
    );
  }

  interceptErrorResponse(request: HttpRequest<any>, errorResponse: HttpErrorResponse): HttpErrorResponse {
    const showToast = !toastErrorCodesHidden.includes(errorResponse.error?.applicationCode);
    switch (errorResponse.status) {
      case 400:
        if (showToast) {
          this.toastService.warning(errorResponse.error?.message || errorResponse.message);
        }

        break;
      case 401:
        if (showToast) {
          this.toastService.warning('', `SI_ERROR_HTTP_${errorResponse.status}`);
        }

        switch (errorResponse.error?.applicationCode) {
          case 'PCS0001':
            this.onUnauthorizedHttpCall(false);
            break;
          default:
            this.onUnauthorizedHttpCall();
        }

        break;
      case 404:
        this.loaderService.stop();
        if (showToast) {
          this.toastService.warning(errorResponse.error?.message || errorResponse.message);
        }

        break;
      case 500:
        this.loaderService.stop();
        if (showToast) {
          this.toastService.error(errorResponse.error?.message || errorResponse.message, `SI_ERROR_HTTP_${errorResponse.status}`);
        }

        if (request.url.endsWith('/api/v1/configurations')) {
          this.onUnauthorizedHttpCall(false);
        }

        break;
      case 502:
        this.loaderService.stop();
        if (showToast) {
          this.toastService.error(errorResponse.error?.message || errorResponse.message);
        }

        break;
      case 0:
      case 504:
        if (showToast) {
          this.toastService.error('SI_ERROR_HTTP_APP_CANNOT_BE_ACCESSED', 'SI_ERROR_HTTP_MESSAGE');
        }

        this.onUnauthorizedHttpCall();
        break;
      default:
        if (showToast) {
          this.toastService.error(errorResponse.error?.message || errorResponse.message, `SI_ERROR_HTTP_${errorResponse.status}`);
        }

    }

    this.activateLoader(request, false);
    return errorResponse;
  }

  onUnauthorizedHttpCall(redirectToLogin = true): void {
    this.loginService.resetLogin(redirectToLogin);
  }

  activateLoader(request, activateFlag = true): void {
    if (request.method === 'GET') {
      return;
    }

    if (activateFlag) {
      this.loaderService.start();
    } else {
      this.loaderService.stop();
    }
  }

  setBackUrl(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({url: this.apiService.setCompleteUrl(request.url)});
  }

  setLanguage(request: HttpRequest<any>): HttpRequest<any> {
    if (request.method) {
      const language = this.translate.currentLang;
      const params = request.params.set('lang', language);
      request = request.clone({params});
    }

    return request;
  }

  setSecurity(request: HttpRequest<any>): HttpRequest<any> {
    const authorization = this.loginService.getAuthorization();
    if (authorization) {
      const headers = new HttpHeaders({'Authorization': 'Bearer ' + authorization});
      return request.clone({headers});
      // return request.clone({headers, withCredentials: true});
    } else {
      return request;
    }
  }
}
